import httpService from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Servicos_SalvarInput.ts';

import '../contracts/Servicos_BuscarCategoriasResponse';
import '../contracts/Servicos_BuscarServicosResponse';
import '../contracts/Servicos_DeleteResponse';
import '../contracts/Servicos_GetByIDResponse';
import '../contracts/Servicos_SalvarResponse';
import type { Servicos_SalvarInput } from '../contracts/Servicos_SalvarInput';
/**
 * Composable for managing Servicos (Services)
 */
export function useServicos() {

  /**
   * Fetch all services with categories
   * @returns {Promise<Array>} List of services
   */
  const fetchServicos = async () => {
    log('useServicos', 'fetchServicos', 'Fetching all services');

    try {
      const response = await httpService.get('/index.php/config/servicos/buscar_servicos', { responseContract: 'Servicos_BuscarServicosResponse' });

      if (response.success) {
        log('useServicos', 'fetchServicos', 'Services fetched successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to fetch services');
      }
    } catch (error) {
      log('useServicos', 'fetchServicos', 'Error fetching services', error);
      throw error;
    }
  };

  /**
   * Fetch a single service by ID
   * @param {Number} servicoId - Service ID
   * @returns {Promise<Object>} Service data
   */
  const fetchServicoById = async (servicoId: number) => {
    log('useServicos', 'fetchServicoById', 'Fetching service', servicoId);

    try {
      const response = await httpService.get(`/index.php/config/servicos/getByID/${servicoId}`, { responseContract: 'Servicos_GetByIDResponse' });

      if (response.success) {
        log('useServicos', 'fetchServicoById', 'Service fetched successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to fetch service');
      }
    } catch (error) {
      log('useServicos', 'fetchServicoById', 'Error fetching service', error);
      throw error;
    }
  };

  /**
   * Save service (create or update)
   * @param {Object} servicoData - Service data
   * @returns {Promise<Object>} Response data
   */
  const saveServico = async (servicoData: Servicos_SalvarInput) => {
    log('useServicos', 'saveServico', 'Saving service', servicoData);

    try {
      validate('Servicos_SalvarInput', servicoData);
      const response = await httpService.post('/index.php/config/servicos/salvar', servicoData, { responseContract: 'Servicos_SalvarResponse' });

      if (response.success) {
        log('useServicos', 'saveServico', 'Service saved successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to save service');
      }
    } catch (error) {
      log('useServicos', 'saveServico', 'Error saving service', error);
      throw error;
    }
  };

  /**
   * Delete a service
   * @param {Number} servicoId - Service ID
   * @returns {Promise<void>}
   */
  const deleteServico = async (servicoId: number) => {
    log('useServicos', 'deleteServico', 'Deleting service', servicoId);

    try {
      const response = await httpService.post(`/index.php/config/servicos/delete/${servicoId}`, null, { responseContract: 'Servicos_DeleteResponse', inputContract: 'Servicos_DeleteInput' });

      if (response.success) {
        log('useServicos', 'deleteServico', 'Service deleted successfully');
        return;
      } else {
        throw new Error(response.error || 'Failed to delete service');
      }
    } catch (error) {
      log('useServicos', 'deleteServico', 'Error deleting service', error);
      throw error;
    }
  };

  /**
   * Fetch all service categories for dropdown
   * @returns {Promise<Array>} List of categories
   */
  const fetchCategorias = async () => {
    log('useServicos', 'fetchCategorias', 'Fetching categories');

    try {
      const response = await httpService.get('/index.php/config/servicos/buscar_categorias', { responseContract: 'Servicos_BuscarCategoriasResponse' });

      if (response.success) {
        log('useServicos', 'fetchCategorias', 'Categories fetched successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to fetch categories');
      }
    } catch (error) {
      log('useServicos', 'fetchCategorias', 'Error fetching categories', error);
      throw error;
    }
  };

  return {
    fetchServicos,
    fetchServicoById,
    saveServico,
    deleteServico,
    fetchCategorias
  };
}
